import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FiMenu, 
  FiX, 
  FiUser, 
  FiLogOut, 
  FiDollarSign, 
  FiShoppingBag, 
  FiSettings,
  FiHome,
  FiGrid,
  FiCreditCard,
  FiList,
  FiBell,
  FiTrendingUp,
  FiSmartphone
} from 'react-icons/fi';
import { BsWallet2 } from 'react-icons/bs';
import { logout } from '../../store/slices/authSlice';
import toast from 'react-hot-toast';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully');
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/', icon: FiHome },
    { name: 'Accounts', path: '/accounts', icon: FiShoppingBag },
    { name: 'Boosting', path: '/social-boost', icon: FiTrendingUp },
    { name: 'Virtual Numbers', path: '/virtual-numbers', icon: FiSmartphone },
    { name: 'How It Works', path: '#how-it-works', icon: FiCreditCard },
  ];

  const userLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: FiUser },
    { name: 'Transactions', path: '/transactions', icon: FiList },
    { name: 'Orders', path: '/orders', icon: FiShoppingBag },
    { name: 'Profile', path: '/profile', icon: FiSettings },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 w-full ${
          scrolled 
            ? 'bg-[#0f172a]/80 backdrop-blur-xl border-b border-blue-500/20 shadow-lg shadow-blue-500/5' 
            : 'bg-[#0f172a] text-white border-blue-500/20'
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group shrink-0">
              <motion.div
                // whileHover={{ rotate: 10 }}
                transition={{ duration: 0.3 }}
                className="hoverLogo w-25 p-2 flex items-center justify-center  shrink-0"
              >
                <img src="/nitrologs.png" alt="Logo" className="w-full h-full text-white" />
              </motion.div>
              {/* <span className="text-lg sm:text-2xl font-display font-bold hidden sm:inline">
                <span className="text-transparent bg-clip-text bg-linear-to-r from-blue-400 to-cyan-400">
                  Nitro
                </span>
                <span className="text-white">Logs</span>
              </span> */}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 lg:space-x-8">
              {navLinks.map((link) => {
                const isHashLink = link.path.startsWith('#');
                const handleHashClick = (e) => {
                  if (isHashLink) {
                    e.preventDefault();
                    const element = document.querySelector(link.path);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }
                };
                
                return isHashLink ? (
                  <a
                    key={link.name}
                    href={link.path}
                    onClick={handleHashClick}
                    className="text-gray-300 hover:text-blue-400 transition-colors relative group font-medium text-sm lg:text-base"
                  >
                    {link.name}
                    <motion.span
                      className="absolute bottom-0 left-0 w-0 h-0.5 bg-linear-to-r from-blue-400 to-cyan-400 group-hover:w-full transition-all duration-300"
                      whileHover={{ width: '100%' }}
                    />
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="text-gray-300 hover:text-blue-400 transition-colors relative group font-medium text-sm lg:text-base"
                  >
                    {link.name}
                    <motion.span
                      className="absolute bottom-0 left-0 w-0 h-0.5 bg-linear-to-r from-blue-400 to-cyan-400 group-hover:w-full transition-all duration-300"
                      whileHover={{ width: '100%' }}
                    />
                  </Link>
                );
              })}
            </div>

            {/* Desktop Right Section */}
            <div className="hidden lg:flex items-center space-x-3 lg:space-x-4">
              {isAuthenticated ? (
                <>
                  {/* Balance */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center space-x-2 px-3 lg:px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl cursor-pointer backdrop-blur-sm text-sm lg:text-base"
                    onClick={() => navigate('/dashboard')}
                  >
                    <BsWallet2 className="w-4 h-4 lg:w-5 lg:h-5 text-blue-400" />
                    <span className="font-semibold text-white hidden sm:inline">
                      ₦{user?.balance?.toLocaleString()}
                    </span>
                  </motion.div>

                  {/* Notifications */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center relative backdrop-blur-sm shrink-0"
                    onClick={() => setShowNotifications(!showNotifications)}
                  >
                    <FiBell className="w-4 h-4 lg:w-5 lg:h-5 text-gray-300" />
                    <span className="absolute top-2 right-2 w-2 h-2 bg-blue-400 rounded-full"></span>
                  </motion.button>

                  {/* User Menu */}
                  <div className="relative group">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center backdrop-blur-sm shrink-0"
                    >
                      <FiUser className="w-4 h-4 lg:w-5 lg:h-5 text-gray-300" />
                    </motion.button>
                    
                    <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-[#0f172a] border border-blue-500/20 rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 backdrop-blur-xl shadow-lg">
                      <div className="py-2">
                        {userLinks.map((link) => (
                          <Link
                            key={link.name}
                            to={link.path}
                            className="flex items-center space-x-3 px-4 py-2 hover:bg-blue-500/10 transition-colors text-gray-300 text-sm"
                          >
                            <link.icon className="w-4 h-4 text-blue-400 shrink-0" />
                            <span>{link.name}</span>
                          </Link>
                        ))}
                        
                        {user?.role === 'admin' && (
                          <>
                            <hr className="border-blue-500/20 my-2" />
                            <Link
                              to="/admin"
                              className="flex items-center space-x-3 px-4 py-2 hover:bg-blue-500/10 transition-colors text-blue-400 font-semibold text-sm"
                            >
                              <FiSettings className="w-4 h-4 shrink-0" />
                              <span>Admin Panel</span>
                            </Link>
                          </>
                        )}
                        
                        <hr className="border-blue-500/20 my-2" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-3 px-4 py-2 w-full text-left hover:bg-red-500/10 transition-colors text-red-400 text-sm"
                        >
                          <FiLogOut className="w-4 h-4 shrink-0" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      className="px-4 lg:px-6 py-2 text-gray-300 hover:text-blue-400 transition-colors font-medium text-sm lg:text-base"
                    >
                      Login
                    </motion.button>
                  </Link>
                  <Link to="/register">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      className="bg-linear-to-r from-blue-600 to-cyan-600 text-white font-semibold py-2 px-4 lg:px-6 rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 text-sm lg:text-base"
                    >
                      Get Started
                    </motion.button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0"
            >
              {isOpen ? <FiX className="w-5 h-5 text-white" /> : <FiMenu className="w-5 h-5 text-white" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed top-16 sm:top-20 left-0 right-0 z-40 lg:hidden w-full overflow-y-auto max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-5rem)]"
          >
            <div className="bg-[#0f172a]/95 backdrop-blur-xl border border-blue-500/20 mx-4 mb-4 rounded-2xl overflow-hidden shadow-xl">
              <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                {/* Navigation Links */}
                {navLinks.map((link) => {
                  const isHashLink = link.path.startsWith('#');
                  const handleHashClick = (e) => {
                    if (isHashLink) {
                      e.preventDefault();
                      setIsOpen(false);
                      const element = document.querySelector(link.path);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  };
                  
                  return isHashLink ? (
                    <a
                      key={link.name}
                      href={link.path}
                      onClick={handleHashClick}
                      className="flex items-center space-x-3 px-3 sm:px-4 py-2 hover:bg-blue-500/10 rounded-xl transition-colors text-gray-300 text-sm sm:text-base"
                    >
                      <link.icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 shrink-0" />
                      <span>{link.name}</span>
                    </a>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center space-x-3 px-3 sm:px-4 py-2 hover:bg-blue-500/10 rounded-xl transition-colors text-gray-300 text-sm sm:text-base"
                    >
                      <link.icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 shrink-0" />
                      <span>{link.name}</span>
                    </Link>
                  );
                })}
                
                {isAuthenticated ? (
                  <>
                    <hr className="border-blue-500/20" />
                    
                    {/* Balance */}
                    <div className="px-3 sm:px-4 py-2 flex items-center space-x-2 text-sm sm:text-base">
                      <BsWallet2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 shrink-0" />
                      <span className="font-semibold text-white">Balance: ₦{user?.balance?.toLocaleString()}</span>
                    </div>

                    {/* User Links */}
                    {userLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-3 px-3 sm:px-4 py-2 hover:bg-blue-500/10 rounded-xl transition-colors text-gray-300 text-sm sm:text-base"
                      >
                        <link.icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 shrink-0" />
                        <span>{link.name}</span>
                      </Link>
                    ))}

                    {/* Admin Link */}
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-3 px-3 sm:px-4 py-2 bg-blue-500/10 text-blue-400 rounded-xl font-semibold text-sm sm:text-base"
                      >
                        <FiSettings className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                        <span>Admin Panel</span>
                      </Link>
                    )}

                    {/* Logout Button */}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2 hover:bg-red-500/10 rounded-xl transition-colors text-red-400 text-sm sm:text-base"
                    >
                      <FiLogOut className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <hr className="border-blue-500/20" />
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="block px-3 sm:px-4 py-2 hover:bg-blue-500/10 rounded-xl transition-colors text-gray-300 text-sm sm:text-base"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsOpen(false)}
                      className="block px-3 sm:px-4 py-2 bg-linear-to-r from-blue-600 to-cyan-600 text-white rounded-xl text-center font-semibold text-sm sm:text-base"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notifications Dropdown */}
      <AnimatePresence>
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-16 sm:top-20 right-4 z-50 w-80 max-w-calc(100vw - 2rem)"
          >
            <div className="bg-[#0f172a]/95 backdrop-blur-xl border border-blue-500/20 rounded-2xl overflow-hidden shadow-xl">
              <div className="p-4 bg-linear-to-r from-blue-500/10 to-cyan-500/10 border-b border-blue-500/20">
                <h3 className="font-semibold text-white text-sm sm:text-base">Notifications</h3>
              </div>
              <div className="p-4">
                <p className="text-gray-400 text-center py-4 text-sm">No new notifications</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};